import { useMemo } from 'react'
import * as Diff from 'diff'
import { cn } from '@/lib/utils'

interface DiffViewerProps {
    currentContent: any // JSON
    versionContent: any // JSON
}

export function DiffViewer({ currentContent, versionContent }: DiffViewerProps) {
    // 1. Extract text from both versions
    // Simplistic extraction: join all text nodes.
    // Ideally we diff prose-mirror node by node, but that's very complex.
    // Text diff provides a "Google Docs" style experience for textual changes.

    const extractText = (content: any): string => {
        try {
            if (!content) return ''

            // Handle if it's already an array (ScriptElement[])
            if (Array.isArray(content)) {
                return content.map((c: any) => extractText(c)).join('\n\n')
            }

            // Handle string (JSON check)
            if (typeof content === 'string') {
                try {
                    const parsed = JSON.parse(content)
                    return extractText(parsed)
                } catch {
                    return content
                }
            }

            // Handle ScriptElement object
            if (content.type && content.content !== undefined) {
                // Formatting based on type could happen here if we wanted markdown-like diffs
                // e.g. return `**${content.content}**` for headings
                // For now just return content
                return content.content
            }

            return ''
        } catch (e) {
            console.error(e)
            return ''
        }
    }

    const diffs = useMemo(() => {
        const text1 = extractText(versionContent)
        const text2 = extractText(currentContent)
        return Diff.diffWords(text1, text2)
    }, [currentContent, versionContent])

    return (
        <div className="flex-1 bg-white dark:bg-zinc-950 h-full overflow-hidden flex flex-col items-center">
            <div className="w-full max-w-4xl h-full p-8 overflow-y-auto font-mono text-sm leading-relaxed whitespace-pre-wrap">
                {diffs.map((part, index) => {
                    const color = part.added ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        part.removed ? 'bg-red-100 text-red-800 line-through decoration-red-500 dark:bg-red-900/30 dark:text-red-300' :
                            'text-foreground'

                    return (
                        <span key={index} className={cn("px-0.5 rounded-sm", color)}>
                            {part.value}
                        </span>
                    )
                })}
            </div>

            <div className="p-2 text-xs text-muted-foreground border-t w-full text-center bg-muted/20">
                <span className="inline-flex items-center gap-1 mr-4">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span> Added
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span> Removed
                </span>
            </div>
        </div>
    )
}
